<template>
	<div class="help-menu">
		<div class="help-menu-content" :class="{ 'is-open': localIsOpen, 'display-none': isHideCompletely, 'is-on-left': isOnLeft }">
			<div class="top-section flex-space-between--center bottom-border font-weight-600">
				<div>Hi {{ userName }}</div>
				<MenuFacesSvg />
			</div>
			<div v-for="item in menuItems" :key="item.text" class="menu-section" @click="item.callback()">
				<Row class="flex-align-center"><component class="mb-[6px]" :is="item.svg" /> {{ item.text }}</Row>
				<div class="flex-centerize">
					<div v-if="item.notification && item.notification() > 0" class="notification-indication flex-centerize mr-1">
						{{ item.notification() }}
					</div>
					<CaretSvg />
				</div>
			</div>
			<div class="bottom-section bottom-top">
				<div class="flex-1 flex-space-between--center">
					<div>
						<a @click="reportBug" class="menu-link hover-effect" target="_blank">Report a bug </a>
						<a @click="openRequestFeature" class="menu-link hover-effect" target="_blank">Request a feature </a>
					</div>
					<div>
						<a href="https://calendly.com/d/2d7-m5y-77k/talk-to-the-ply-team" class="menu-link hover-effect" target="_blank">
							Schedule a help call <ExpandSvg />
						</a>
						<a href="https://ply.canny.io/changelog" class="menu-link hover-effect" target="_blank">Product updates <ExpandSvg /></a>
					</div>
				</div>
			</div>
		</div>
		<IFrameContainer v-if="isOpen" :docsUrl="docsUrl" :isOpen="!!docsUrl" :isOnLeft="isOnLeft" :isNudgeTop="isGuidedTutorialOn" />
		<GuidedTutorialsMenu
			:isOpen="isOpenGuidedTutorialsMenu"
			:isOnLeft="isOnLeft"
			:isNudgeTop="isGuidedTutorialOn"
			@setIsOpen="(v) => $emit('setIsOpen', v)"
		/>
	</div>
</template>

<script>
import GuidedTutorialsMenu from './GuidedTutorialsMenu';
import IFrameContainer from './IFrameContainer';
import CaretSvg from './assets/CaretSvg';
import ChatSvg from './assets/ChatSvg';
import CommunitySvg from './assets/CommunitySvg';
import DocsSvg from './assets/DocsSvg';
import ExpandSvg from './assets/ExpandSvg';
import GuidedTutorialSvg from './assets/GuidedTutorialSvg';
import MenuFacesSvg from './assets/MenuFacesSvg';
import InAppHelpMenuService from './in-app-help-menu-service';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { user } from '@/store/modules/auth/getters';
import { setDocsOpen } from '@/store/modules/onboarding/actions';
import { isGuidedTutorialOn, docsUrl } from '@/store/modules/onboarding/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import IntercomService from '@/utils/IntercomService';
import { capitalizeWord } from '@/utils/language-service';
//import UserbackService from '@/utils/UserbackService';

const menuItem = (text, svg, callback, notification) => ({ text, svg, callback, notification });

export default {
	name: 'InAppHelpMenu',
	components: { IFrameContainer, GuidedTutorialsMenu, MenuFacesSvg, CaretSvg, ExpandSvg },
	props: {
		isOpen: { type: Boolean },
		isOnLeft: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ user, isGuidedTutorialOn, docsUrl }),
		userName() {
			return capitalizeWord(this.user.first_name);
		},
	},
	methods: {
		...mapActionsShorthand({ setDocsOpen }),
		dispacher(callback) {
			return () => {
				setTimeout(() => {
					this.localIsOpen = false;
					this.isHideCompletely = true;
				}, 500);
				callback();
			};
		},
		openIframe(src) {
			this.setDocsOpen(src);
		},
		reportBug() {
			IntercomService.showNewMessage('Hello, I would like to report a bug: \n');
			// UserbackService.open();
		},
		openRequestFeature() {
			AppDialogManagerService.openIntegrationDialog();
		},
		openGuidedTutorials() {
			this.isOpenGuidedTutorialsMenu = true;
		},
		openCommunity() {
			InAppHelpMenuService.openCommunity();
		},
	},
	watch: {
		isOpen: {
			handler(newV) {
				if (!newV) {
					IntercomService.hideChatWindow();
					this.isOpenGuidedTutorialsMenu = false;
					this.setDocsOpen('');
				}
				if (newV) {
					this.isHideCompletely = false;
					setTimeout(() => (this.localIsOpen = true), 100);
				} else {
					this.localIsOpen = false;
					setTimeout(() => (this.isHideCompletely = true), 100);
				}
			},
		},
		docsUrl: {
			handler(newV) {
				this.$emit('setIsOpen', !!newV);
			},
		},
	},
	mounted() {
		IntercomService.registerCountChangeCallback((c) => {
			this.count = c;
		});
	},
	data() {
		return {
			menuItems: [
				menuItem(
					'Video tutorials',
					GuidedTutorialSvg,
					this.dispacher(() => this.openIframe('https://videos.ply.io/watch/gallery/Mzc5MA'))
				),
				menuItem(
					'Docs',
					DocsSvg,
					this.dispacher(() => this.openIframe('https://docs.ply.io/welcome'))
				),
				menuItem('Community', CommunitySvg, this.dispacher(this.openCommunity)),
				menuItem('Chat with us', ChatSvg, this.dispacher(IntercomService.show), () => this.count),
				// menuItem('Academy', AcademySvg),
			],
			localIsOpen: true,
			isHideCompletely: true,
			isOpenGuidedTutorialsMenu: false,
			count: 0,
		};
	},
};
</script>

<style lang="scss" scoped>
@import './in-app-help-mixin.scss';

.help-menu {
	.help-menu-content {
		width: 345px;
		background-color: #ffffff;
		border-radius: 16px;
		border: 1px solid #dfe4ef;
		box-shadow: 0 3px 51px 0 #80899c59;

		@include open-close-behaviour();

		.top-section {
			padding: 14px 19px;
		}

		.menu-section {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 12px 18px;

			::v-deep(svg) {
				transform: translate(0, 3px);
				margin-right: 5px;
			}

			&:hover {
				background-color: #fbfbfc;
				cursor: pointer;
			}
		}

		.menu-link {
			display: block;
			margin: 3px 0 8px 0;
			color: var(--primary-font-color) !important;
			text-decoration: none;
			font-weight: 300;
			transition: 0.3s;

			::v-deep(svg) {
				opacity: 0;
			}

			&:hover {
				cursor: pointer;
				color: #888888 !important;
				transition: 0.2s;

				&::v-deep(svg) {
					opacity: 1;
				}
			}
		}

		.bottom-section {
			padding: 12px 18px;
			font-size: 14px;
		}

		.bottom-border {
			border-bottom: 1px solid #dfe4ef;
		}

		.bottom-top {
			border-top: 1px solid #dfe4ef;
		}

		.notification-indication {
			width: 23px;
			height: 23px;
			margin: 4px 11px 0 0;
			background-color: #fc3a5b;
			border-radius: 50%;
			text-align: center;
			font-size: 12px;
		}
	}
}
</style>
